/**
 * Diff a nivel de PALABRA para el historial de versiones (L4).
 *
 * Pieza AGNÓSTICA (guía VERSION_HISTORY): no sabe nada de entradas de diario;
 * compara dos textos y devuelve segmentos `equal | added | removed` listos
 * para pintar (added = está en el texto nuevo, removed = solo en el viejo).
 *
 * Algoritmo: LCS clásico por programación dinámica sobre tokens
 * (palabras + separadores). O(n·m) — suficiente para textos de páginas wiki.
 * Salvaguarda: si n·m supera un tope, se recorta el prefijo/sufijo común y
 * el resto se marca como reemplazo completo (nunca cuelga la UI).
 */

export type DiffSegmentType = "equal" | "added" | "removed";

export interface DiffSegment {
  type: DiffSegmentType;
  text: string;
}

/** Tope de trabajo del DP (tokens_viejo × tokens_nuevo). */
const MAX_DP_CELLS = 1_000_000;

/** Trocea en palabras y separadores (los espacios/saltos se conservan). */
export function tokenizeWords(text: string): string[] {
  if (!text) {
    return [];
  }
  return text.split(/(\s+)/u).filter((t) => t.length > 0);
}

function pushSegment(out: DiffSegment[], type: DiffSegmentType, text: string) {
  if (!text) {
    return;
  }
  const last = out.at(-1);
  if (last?.type === type) {
    last.text += text;
  } else {
    out.push({ type, text });
  }
}

/**
 * Diff palabra a palabra entre `oldText` (versión) y `newText` (actual).
 * Devuelve los segmentos en orden de lectura del texto NUEVO, con los
 * eliminados intercalados donde estaban.
 */
export function diffWords(oldText: string, newText: string): DiffSegment[] {
  if (oldText === newText) {
    return oldText ? [{ type: "equal", text: oldText }] : [];
  }

  let a = tokenizeWords(oldText);
  let b = tokenizeWords(newText);
  const out: DiffSegment[] = [];

  // Recorte de prefijo/sufijo común: abarata el DP y es lo habitual al
  // editar un diario (se añade al final / se toca un párrafo).
  let prefixLength = 0;
  while (
    prefixLength < a.length &&
    prefixLength < b.length &&
    a[prefixLength] === b[prefixLength]
  ) {
    prefixLength += 1;
  }
  let suffixLength = 0;
  while (
    suffixLength < a.length - prefixLength &&
    suffixLength < b.length - prefixLength &&
    a[a.length - 1 - suffixLength] === b[b.length - 1 - suffixLength]
  ) {
    suffixLength += 1;
  }
  const prefix = a.slice(0, prefixLength).join("");
  const suffix = suffixLength > 0 ? a.slice(a.length - suffixLength).join("") : "";
  a = a.slice(prefixLength, a.length - suffixLength);
  b = b.slice(prefixLength, b.length - suffixLength);

  pushSegment(out, "equal", prefix);

  if (a.length * b.length > MAX_DP_CELLS) {
    // Texto desmesurado: reemplazo en bloque (correcto aunque menos fino).
    pushSegment(out, "removed", a.join(""));
    pushSegment(out, "added", b.join(""));
    pushSegment(out, "equal", suffix);
    return out;
  }

  // LCS por DP: dp[i][j] = longitud de la LCS de a[i..] y b[j..].
  const n = a.length;
  const m = b.length;
  const width = m + 1;
  const dp = new Uint32Array((n + 1) * width);
  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      dp[i * width + j] =
        a[i] === b[j]
          ? (dp[(i + 1) * width + j + 1] ?? 0) + 1
          : Math.max(dp[(i + 1) * width + j] ?? 0, dp[i * width + j + 1] ?? 0);
    }
  }

  // Reconstrucción: emite eliminados antes que añadidos en cada divergencia.
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    const tokenA = a[i] ?? "";
    const tokenB = b[j] ?? "";
    if (tokenA === tokenB) {
      pushSegment(out, "equal", tokenA);
      i += 1;
      j += 1;
    } else if ((dp[(i + 1) * width + j] ?? 0) >= (dp[i * width + j + 1] ?? 0)) {
      pushSegment(out, "removed", tokenA);
      i += 1;
    } else {
      pushSegment(out, "added", tokenB);
      j += 1;
    }
  }
  while (i < n) {
    pushSegment(out, "removed", a[i] ?? "");
    i += 1;
  }
  while (j < m) {
    pushSegment(out, "added", b[j] ?? "");
    j += 1;
  }

  pushSegment(out, "equal", suffix);
  return out;
}

/** Resumen del diff: palabras (no separadores) añadidas y eliminadas. */
export function diffStats(segments: DiffSegment[]): { added: number; removed: number } {
  let added = 0;
  let removed = 0;
  for (const segment of segments) {
    if (segment.type === "equal") {
      continue;
    }
    const words = segment.text.split(/\s+/u).filter((w) => w.length > 0).length;
    if (segment.type === "added") {
      added += words;
    } else {
      removed += words;
    }
  }
  return { added, removed };
}
